import { useEffect, useMemo, useRef, useState } from "react";
import { IPersonaProps } from "../components/shared/assignee/AssigneeFluent9";
import { FormValidationHelpers } from "../helpers/FormValidationHelpers";
import { IProjectsService } from "../services/NewformaApi/IProjectsService";
import { Logger } from "../services/Logger";
import { MailboxItem, OfficeWrapper } from "../services/OfficeWrapper";
import { ExpiredSessionError } from "../models/ExpiredSessionError";

export interface UseProjectTeamMembersOptions {
    projectNrn: string | undefined;
    projectsService: IProjectsService;
    formValidationHelpers: FormValidationHelpers;
    logger: Logger;
    onExpiredSession?: () => void;
    // Autopopulation inputs (optional). When both are provided, the hook derives suggested From/To/CC
    // selections from the current email (From = sender, To/CC = recipients) so a consumer can seed its
    // own controlled state once per email. The hook never holds the live selection, so the consumer
    // remains the single source of truth and there is no two-sources conflict.
    officeWrapper?: OfficeWrapper;
    mailboxItem?: MailboxItem | null;
}

export interface UseProjectTeamMembersResult {
    teamMembers: IPersonaProps[];
    isLoadingTeamMembers: boolean;
    // One-time suggestions derived from the current email. A new identity is produced only when the
    // mailbox item changes, which is exactly when the consumer should (re)seed its From/To/CC state.
    autoPopulatedFrom: IPersonaProps[];
    autoPopulatedTo: IPersonaProps[];
    autoPopulatedCc: IPersonaProps[];
}

// Stable empty array so consumers that don't opt into autopopulation keep a constant identity.
const EMPTY_PERSONAS: IPersonaProps[] = [];

// Loads the full project team (members and groups) as persona props for the Fluent 9 From/To/CC
// pickers. The list is fetched once per project selection and filtered client-side by the pickers,
// so there are no per-keystroke requests. This is the single source of truth for the options; the
// consuming pickers stay presentational and receive the result as a prop.
export function useProjectTeamMembers(options: UseProjectTeamMembersOptions): UseProjectTeamMembersResult {
    const {
        projectNrn,
        projectsService,
        formValidationHelpers,
        logger,
        onExpiredSession,
        officeWrapper,
        mailboxItem,
    } = options;

    const [teamMembers, setTeamMembers] = useState<IPersonaProps[]>([]);
    const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState<boolean>(false);

    // Parents typically pass freshly bound callbacks on every render (e.g. onExpiredSession); reading
    // them through refs keeps a parent re-render (like showing a toast) from refetching the team.
    const collaboratorsRef = useRef({ formValidationHelpers, logger, onExpiredSession });
    collaboratorsRef.current = { formValidationHelpers, logger, onExpiredSession };

    useEffect(() => {
        if (!projectNrn) {
            setTeamMembers([]);
            setIsLoadingTeamMembers(false);
            return;
        }

        let isCancelled = false;
        setIsLoadingTeamMembers(true);
        (async () => {
            try {
                const members = await projectsService.getAllTeamMembersNormalized(projectNrn, "");
                if (isCancelled) {
                    return;
                }
                setTeamMembers(collaboratorsRef.current.formValidationHelpers.getPersonaPropsFromTeamMembers(members));
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                if (ExpiredSessionError.isInstanceOf(error)) {
                    collaboratorsRef.current.onExpiredSession?.();
                    return;
                }
                collaboratorsRef.current.logger.error("useProjectTeamMembers - failed to load team members", error);
                setTeamMembers([]);
            } finally {
                if (!isCancelled) {
                    setIsLoadingTeamMembers(false);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [projectNrn, projectsService]);

    // Derive the From/To/CC suggestions from the current email via OfficeWrapper (which owns all Office
    // access and the auto-file-address filtering). Recomputed only when the mailbox item changes, so a
    // consumer keyed on the same item seeds its own state exactly once per email. This is a pure read
    // (no state here), so the consumer stays the single source of truth for the selection.
    const autoPopulated = useMemo(() => {
        if (!officeWrapper || !mailboxItem) {
            return { from: EMPTY_PERSONAS, to: EMPTY_PERSONAS, cc: EMPTY_PERSONAS };
        }
        return {
            from: officeWrapper.getFromPersonaProps(),
            to: officeWrapper.getToPersonaProps(),
            cc: officeWrapper.getCCPersonaProps(),
        };
    }, [officeWrapper, mailboxItem]);

    return {
        teamMembers,
        isLoadingTeamMembers,
        autoPopulatedFrom: autoPopulated.from,
        autoPopulatedTo: autoPopulated.to,
        autoPopulatedCc: autoPopulated.cc,
    };
}
